import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import useModalInactiveState from '@fabric-app/hooks/useModalInactiveState';
import { useChatbot } from '../../hooks/useChatbot.js';
import { ChatbotErrorCodes } from '@fabric/woody-client';
import React__default, { useState, useMemo } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { framerAnimationSlideUp } from '@fabric-app/ui/constants/framerAnimations';
import { Conversation } from '@fabric-app/ui/components/Conversation';
import { useQueryResourceDetail } from '../../../resources/queries/useQueryResourceDetail.js';
import { AssistantContextSubType } from '../../types.js';
import { getResourceGenericName } from '../../../resources/utils/getResourceGenericName.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { P } from '@fabric-app/ui/components/Typography';
import { useActiveContextFileProcessing } from '../../hooks/useActiveContextFileProcessing.js';
import { IconNavigational24UpArrow } from '@fabric-app/icons/svg';
import { useAuthStatus } from '@fabric-app/compat';
import { Button } from '@fabric-app/ui/components/Button';
import { usePostSigninRedirect } from '../../../auth/hooks/usePostSigninRedirect.js';
import { ForcedTheme } from '@fabric-app/ui/theme/ForcedTheme';
import { AssistantQuickActions } from '../AssistantQuickActions.js';

const MessageForm = /* @__PURE__ */ React__default.forwardRef(({ autoFocus: _autoFocus = false }, propRef) => {
  const [content, setContent] = useState("");
  const authStatus = useAuthStatus();
  const { addMessage, isLoading: isLoadingAnswer, error, usage, activeContext } = useChatbot();
  const { redirectToSignin } = usePostSigninRedirect();
  const { subType, value } = activeContext;
  const { resource } = useQueryResourceDetail(Array.isArray(value) ? value[0] : void 0, {
    placeholderData: void 0,
    enabled: subType === AssistantContextSubType.File
  });
  const placeholder = useMemo(() => {
    if (subType === AssistantContextSubType.Selection) return "Ask AI about current selection...";
    if (resource) return `Ask AI about this ${getResourceGenericName(resource)}`;
    return "Ask your AI to find something...";
  }, [
    resource,
    subType
  ]);
  const [textAreaRef, setTextAreaRef] = useState(null);
  const { isMobileView } = useResponsive();
  const formRef = React__default.useRef(null);
  const ref = useMemo(() => mergeRefs([
    propRef,
    setTextAreaRef
  ]), [
    propRef
  ]);
  const isContextFileProcessing = useActiveContextFileProcessing();
  const [hasShownFileProcessing, setHasShownFileProcessing] = useState(isContextFileProcessing);
  useRunOnce(() => {
    setHasShownFileProcessing(true);
  }, {
    condition: isContextFileProcessing
  });
  const autoFocus = _autoFocus && !hasShownFileProcessing;
  const handleSubmit = async (e) => {
    if (usage && usage.used >= (usage.limit ?? 3) || isContextFileProcessing) {
      return;
    }
    if (isLoadingAnswer) return;
    e?.preventDefault();
    addMessage(content);
    setContent("");
    textAreaRef?.focus();
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const isModalInactive = useModalInactiveState(formRef);
  useRunOnce(() => {
    if (!autoFocus) return;
    window.requestAnimationFrame(() => {
      textAreaRef?.focus();
    });
  }, {
    condition: Boolean(textAreaRef)
  });
  const hiddenInput = isModalInactive && isMobileView && autoFocus;
  const isOverUsageLimit = usage && usage.used >= (usage.limit ?? 10) || false;
  return /* @__PURE__ */ jsxs(Conversation.Form, {
    ...framerAnimationSlideUp,
    ref: formRef,
    children: [
      isOverUsageLimit && /* @__PURE__ */ jsx(P, {
        size: "sm",
        style: {
          lineHeight: "22px"
        },
        children: "Assistant usage limit reached"
      }),
      /* @__PURE__ */ jsx(AssistantQuickActions, {}),
      authStatus !== "authenticated" && isOverUsageLimit ? /* @__PURE__ */ jsx(Button, {
        variant: "primary-inverse",
        style: {
          width: "100%"
        },
        onClick: () => {
          redirectToSignin();
        },
        children: "Log in to continue using Assistant"
      }) : /* @__PURE__ */ jsx(ForcedTheme, {
        colorScheme: "light",
        asChild: true,
        children: /* @__PURE__ */ jsx(Conversation.FormField, {
          children: isContextFileProcessing ? /* @__PURE__ */ jsxs(Fragment, {
            children: [
              /* @__PURE__ */ jsx(Spinner, {
                size: 16
              }),
              /* @__PURE__ */ jsx(P, {
                size: "xs",
                color: "placeholder",
                style: {
                  lineHeight: "24px"
                },
                children: "Processing this file, one sec..."
              })
            ]
          }) : /* @__PURE__ */ jsxs(Fragment, {
            children: [
              !hiddenInput && /* @__PURE__ */ jsx(Conversation.FormTextArea, {
                autoFocus,
                placeholder,
                ref,
                onChange: (e) => setContent(e.target.value),
                value: content,
                onKeyDown: handleKeyDown
              }),
              /* @__PURE__ */ jsx(Conversation.FormButton, {
                onClick: handleSubmit,
                disabled: content === "" || isLoadingAnswer || error === ChatbotErrorCodes.USAGE_LIMIT_EXCEEDED,
                children: /* @__PURE__ */ jsx(IconNavigational24UpArrow, {})
              })
            ]
          })
        })
      })
    ]
  });
});
MessageForm.displayName = "MessageForm";

export { MessageForm as default };
//# sourceMappingURL=MessageForm.js.map
